// src/components/AIService.jsx
import React, { useState } from 'react';
import AIService from '../services/aiservice';
import { FaSearch, FaSun, FaSnowflake, FaUmbrellaBeach, FaMountain, FaCity, FaLeaf } from 'react-icons/fa';

const AIServiceComponent = () => {
  const [activeTab, setActiveTab] = useState('destination');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Destination Finder Form State
  const [destinationData, setDestinationData] = useState({
    season: '',
    budget: 'moderate',
    interests: '',
    duration: '5-7',
    travelCompanions: 'solo',
    temperaturePreference: 'moderate'
  });

  // Weather Guide Form State
  const [weatherData, setWeatherData] = useState({
    month: new Date().getMonth() + 1,
    temperaturePreference: 'moderate',
    activities: ''
  });

  // Handle destination recommendations
  const handleGetRecommendations = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const data = {
        ...destinationData,
        interests: destinationData.interests.split(',').map(i => i.trim()).filter(i => i)
      };
      
      const response = await AIService.getDestinationRecommendations(data);
      setResult({
        type: 'destination',
        data: response.data
      });
    } catch (err) {
      setError(err.message || 'Failed to get recommendations');
    } finally {
      setLoading(false);
    }
  };

  // Handle weather-based recommendations
  const handleGetWeatherDestinations = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const data = {
        ...weatherData,
        activities: weatherData.activities.split(',').map(i => i.trim()).filter(i => i)
      };
      
      const response = await AIService.getWeatherBasedDestinations(data);
      setResult({
        type: 'weather',
        data: response.data
      });
    } catch (err) {
      setError(err.message || 'Failed to get weather destinations');
    } finally {
      setLoading(false);
    }
  };

  // Get season icon
  const getSeasonIcon = (season) => {
    switch(season) {
      case 'winter': return <FaSnowflake className="text-blue-400" />;
      case 'spring': return <FaLeaf className="text-green-400" />;
      case 'summer': return <FaSun className="text-yellow-400" />;
      case 'autumn': return <FaLeaf className="text-orange-400" />;
      default: return <FaSearch />;
    }
  };

  // Get activity icon
  const getActivityIcon = (activity) => {
    if (activity.includes('beach')) return <FaUmbrellaBeach className="text-blue-400" />;
    if (activity.includes('mountain') || activity.includes('hiking')) return <FaMountain className="text-green-400" />;
    if (activity.includes('city') || activity.includes('urban')) return <FaCity className="text-gray-400" />;
    return <FaSearch />;
  };

  // Render destination results
  const renderDestinationResults = () => {
    if (!result || result.type !== 'destination') return null;
    
    const { recommendations, criteria } = result.data;
    
    if (!recommendations || (Array.isArray(recommendations) && recommendations.length === 0)) {
      return (
        <div className="text-center py-8">
          <p className="text-gray-600">No destinations found. Try different criteria.</p>
        </div>
      );
    }

    if (Array.isArray(recommendations)) {
      return (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-xl">
            <h4 className="font-semibold text-purple-800 mb-2">Your Search Criteria:</h4>
            <div className="flex flex-wrap gap-2">
              {criteria.season && (
                <span className="inline-flex items-center gap-1 bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">
                  {getSeasonIcon(criteria.season)}
                  {criteria.season}
                </span>
              )}
              <span className="inline-flex items-center bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                {criteria.budget} budget
              </span>
              {criteria.interests && criteria.interests.length > 0 && (
                <span className="inline-flex items-center bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                  {criteria.interests}
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {recommendations.slice(0, 6).map((dest, index) => (
              <div 
                key={index} 
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">{dest.name}</h3>
                      <p className="text-gray-600 text-sm">{dest.country}</p>
                    </div>
                    <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      #{index + 1}
                    </span>
                  </div>

                  {dest.matchReasons && dest.matchReasons.length > 0 && (
                    <div className="mb-4">
                      <p className="text-gray-700 mb-2">{dest.matchReasons.join(' • ')}</p>
                    </div>
                  )}

                  {dest.bestActivities && dest.bestActivities.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-700 mb-2">Top Activities:</h4>
                      <div className="flex flex-wrap gap-2">
                        {dest.bestActivities.slice(0, 3).map((activity, idx) => (
                          <span 
                            key={idx} 
                            className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                          >
                            {getActivityIcon(activity.toLowerCase())}
                            {activity}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between text-sm">
                    <div className="text-gray-600">
                      {dest.costRange && (
                        <span className="font-semibold">
                          ${dest.costRange.budget || '??'} - ${dest.costRange.luxury || '??'}
                        </span>
                      )}
                    </div>
                    {dest.bestMonths && (
                      <span className="text-gray-500">
                        Best: {dest.bestMonths.slice(0, 2).join(', ')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Fallback for non-array response
    return (
      <div className="bg-white rounded-xl p-6 shadow-lg">
        <pre className="whitespace-pre-wrap text-gray-700">{JSON.stringify(recommendations, null, 2)}</pre>
      </div>
    );
  };

  // Render weather results
  const renderWeatherResults = () => {
    if (!result || result.type !== 'weather') return null;
    
    const { destinations, season, month } = result.data;
    
    if (!destinations || (Array.isArray(destinations) && destinations.length === 0)) {
      return (
        <div className="text-center py-8">
          <p className="text-gray-600">No weather-based destinations found.</p>
        </div>
      );
    }

    if (Array.isArray(destinations)) {
      return (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-xl">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-bold text-blue-800 text-lg">{season} Destinations</h4>
                <p className="text-blue-600">Month: {new Date(2000, month - 1, 1).toLocaleString('default', { month: 'long' })}</p>
              </div>
              <div className="text-3xl">
                {getSeasonIcon(season.toLowerCase())}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {destinations.slice(0, 4).map((dest, index) => (
              <div 
                key={index} 
                className="bg-gradient-to-br from-white to-blue-50 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-blue-100"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">{dest.name}</h3>
                      <p className="text-gray-600">{dest.country}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-blue-600">
                        {dest.avgTemp ? `${dest.avgTemp.min}° - ${dest.avgTemp.max}°C` : 'N/A'}
                      </div>
                      <div className="text-sm text-gray-500">Temperature</div>
                    </div>
                  </div>

                  {dest.whyPerfect && (
                    <p className="text-gray-700 mb-4 italic">"{dest.whyPerfect}"</p>
                  )}

                  {dest.topActivities && dest.topActivities.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-700 mb-2">Must-Do Activities:</h4>
                      <div className="space-y-2">
                        {dest.topActivities.map((activity, idx) => (
                          <div key={idx} className="flex items-center gap-3">
                            <div className="w-2 h-2 rounded-full bg-blue-400"></div>
                            <span className="text-gray-600">{activity}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {dest.packingEssentials && dest.packingEssentials.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-700 mb-2">Packing Essentials:</h4>
                      <div className="flex flex-wrap gap-2">
                        {dest.packingEssentials.slice(0, 5).map((item, idx) => (
                          <span 
                            key={idx} 
                            className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm"
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="text-sm text-gray-600">Estimated cost for 7 days:</span>
                        <div className="flex gap-2 mt-1">
                          {dest.weeklyCostEstimate && (
                            <>
                              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">${dest.weeklyCostEstimate.budget || '??'}</span>
                              <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">${dest.weeklyCostEstimate.moderate || '??'}</span>
                              <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">${dest.weeklyCostEstimate.luxury || '??'}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Fallback for non-array response
    return (
      <div className="bg-white rounded-xl p-6 shadow-lg">
        <pre className="whitespace-pre-wrap text-gray-700">{JSON.stringify(destinations, null, 2)}</pre>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
          🧠 AI Travel Assistant
        </h1>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Discover perfect destinations based on your preferences and weather conditions
        </p>
      </div>

      {/* Tabs */}
      <div className="flex justify-center mb-8">
        <div className="inline-flex rounded-2xl bg-gray-100 p-1">
          <button
            onClick={() => setActiveTab('destination')}
            className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center gap-2 ${
              activeTab === 'destination'
                ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                : 'text-gray-600 hover:text-purple-600'
            }`}
          >
            <FaSearch />
            Destination Finder
          </button>
          <button
            onClick={() => setActiveTab('weather')}
            className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center gap-2 ${
              activeTab === 'weather'
                ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg'
                : 'text-gray-600 hover:text-blue-600'
            }`}
          >
            <FaSun />
            Weather Guide
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
        {/* Forms */}
        <div className="p-8">
          {activeTab === 'destination' ? (
            <form onSubmit={handleGetRecommendations} className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                  <FaSearch className="text-purple-500" />
                  Find Your Perfect Destination
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Season */}
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Preferred Season
                    </label>
                    <select
                      value={destinationData.season}
                      onChange={(e) => setDestinationData({...destinationData, season: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all outline-none"
                    >
                      <option value="">Any Season</option>
                      <option value="winter">❄️ Winter (Dec-Feb)</option>
                      <option value="spring">🌸 Spring (Mar-May)</option>
                      <option value="summer">☀️ Summer (Jun-Aug)</option>
                      <option value="autumn">🍂 Autumn (Sep-Nov)</option>
                    </select>
                  </div>

                  {/* Budget */}
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Budget Level
                    </label>
                    <select
                      value={destinationData.budget}
                      onChange={(e) => setDestinationData({...destinationData, budget: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all outline-none"
                    >
                      <option value="budget">💰 Budget ($50-100/day)</option>
                      <option value="moderate">💵 Moderate ($100-250/day)</option>
                      <option value="luxury">💎 Luxury ($250+/day)</option>
                    </select>
                  </div>

                  {/* Trip Duration */}
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Trip Duration
                    </label>
                    <select
                      value={destinationData.duration}
                      onChange={(e) => setDestinationData({...destinationData, duration: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all outline-none"
                    >
                      <option value="3-5">3-5 Days</option>
                      <option value="5-7">5-7 Days</option>
                      <option value="7-10">7-10 Days</option>
                      <option value="10+">10+ Days</option>
                    </select>
                  </div>

                  {/* Travel Companions */}
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Travel With
                    </label>
                    <select
                      value={destinationData.travelCompanions}
                      onChange={(e) => setDestinationData({...destinationData, travelCompanions: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all outline-none"
                    >
                      <option value="solo">👤 Solo Traveler</option>
                      <option value="couple">👫 Couple/Romantic</option>
                      <option value="family">👨‍👩‍👧‍👦 Family</option>
                      <option value="friends">👥 Friends Group</option>
                    </select>
                  </div>

                  {/* Temperature Preference */}
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Temperature Preference
                    </label>
                    <select
                      value={destinationData.temperaturePreference}
                      onChange={(e) => setDestinationData({...destinationData, temperaturePreference: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all outline-none"
                    >
                      <option value="cool">❄️ Cool (10-20°C)</option>
                      <option value="moderate">🌡️ Moderate (20-30°C)</option>
                      <option value="warm">☀️ Warm (30-35°C)</option>
                      <option value="hot">🔥 Hot (35°C+)</option>
                    </select>
                  </div>

                  {/* Interests */}
                  <div className="space-y-2 md:col-span-2 lg:col-span-1">
                    <label className="block text-sm font-semibold text-gray-700">
                      Interests (comma separated)
                    </label>
                    <input
                      type="text"
                      value={destinationData.interests}
                      onChange={(e) => setDestinationData({...destinationData, interests: e.target.value})}
                      placeholder="e.g., beach, hiking, culture, food, adventure"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all outline-none"
                    />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-xl hover:shadow-xl hover:scale-[1.02] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Finding Perfect Destinations...
                  </>
                ) : (
                  <>
                    <FaSearch />
                    Find My Perfect Destinations
                  </>
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleGetWeatherDestinations} className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                  <FaSun className="text-yellow-500" />
                  Weather-Based Destination Guide
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Month */}
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Travel Month
                    </label>
                    <select
                      value={weatherData.month}
                      onChange={(e) => setWeatherData({...weatherData, month: parseInt(e.target.value)})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
                    >
                      {Array.from({length: 12}, (_, i) => i + 1).map(month => (
                        <option key={month} value={month}>
                          {new Date(2000, month - 1, 1).toLocaleString('default', { month: 'long' })}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Temperature Preference */}
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Temperature Preference
                    </label>
                    <select
                      value={weatherData.temperaturePreference}
                      onChange={(e) => setWeatherData({...weatherData, temperaturePreference: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
                    >
                      <option value="cool">❄️ Cool (10-20°C)</option>
                      <option value="moderate">🌡️ Moderate (20-30°C)</option>
                      <option value="warm">☀️ Warm (30-35°C)</option>
                      <option value="hot">🔥 Hot (35°C+)</option>
                    </select>
                  </div>

                  {/* Activities */}
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Preferred Activities
                    </label>
                    <input
                      type="text"
                      value={weatherData.activities}
                      onChange={(e) => setWeatherData({...weatherData, activities: e.target.value})}
                      placeholder="e.g., skiing, beach, hiking, sightseeing"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
                    />
                  </div>
                </div>

                <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-100">
                  <p className="text-blue-700 text-sm">
                    💡 <span className="font-semibold">Tip:</span> We'll find destinations perfect for your chosen month and activities, with optimal weather conditions!
                  </p>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold rounded-xl hover:shadow-xl hover:scale-[1.02] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Analyzing Weather Conditions...
                  </>
                ) : (
                  <>
                    <FaSun />
                    Find Weather-Perfect Destinations
                  </>
                )}
              </button>
            </form>
          )}
        </div>

        {/* Loading & Error States */}
        {loading && (
          <div className="p-8 text-center">
            <div className="inline-flex flex-col items-center">
              <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mb-4"></div>
              <p className="text-gray-600 font-medium">
                {activeTab === 'destination' 
                  ? 'Searching for perfect destinations...' 
                  : 'Analyzing weather patterns...'}
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="mx-8 mb-8 p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-start gap-3">
              <div className="text-red-500 text-xl">⚠️</div>
              <div>
                <h4 className="font-semibold text-red-700">Error</h4>
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Results Section */}
        {result && !loading && (
          <div className="border-t border-gray-200">
            <div className="p-8">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-2xl font-bold text-gray-800">
                  {result.type === 'destination' ? '✨ Recommended Destinations' : '☀️ Weather-Perfect Destinations'}
                </h3>
                <button
                  onClick={() => setResult(null)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Clear Results
                </button>
              </div>
              
              {result.type === 'destination' ? renderDestinationResults() : renderWeatherResults()}
            </div>
          </div>
        )}
      </div>

      {/* Info Footer */}
      <div className="mt-8 text-center text-gray-500 text-sm">
        <p>💡 Using AI to match your preferences with ideal travel destinations</p>
      </div>
    </div>
  );
};

export default AIServiceComponent;